import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useFilterStore from "../../store/useFilterStore";
import useTableStore from "../../store/useTabStore";
import IconArrow from "../icons/iconArrow";
import {
  CompositionIcon,
  ExperimentalPropertiesIcon,
  FormulationsIcon,
  LibraryIcon,
  LipidPropertiesIcon,
} from "../icons/icons";
import Dropdown from "../ui/dropdown";
import RangeSlider from "../ui/slider";

interface AccordionState {
  "Library Code": boolean;
  Composition: boolean;
  Formulations: boolean;
  "Ionizable Lipid Properties": boolean;
  "Experimental Properties": boolean;
  "Biodegradable substructures": boolean;
  Source: boolean;
}

interface RangeValue {
  min: number;
  max: number;
}

const isRangeValue = (value: any): value is { min: number; max: number } =>
  typeof value === "object" && "min" in value && "max" in value;

const getSliderLabel = (key: string): string => {
  switch (key) {
    case "nitrogen_count":
      return "# Nitrogen";
    case "heavy_atoms":
      return "# Heavy Atoms";
    case "rings":
      return "# Rings";
    case "aromatic_rings":
      return "# Aromatic Rings";
    case "rotatable_bonds":
      return "# Rotatable Bonds";
    case "van_der_waals_molecular_volume":
      return "van der Waals Molecular Volume";
    case "hydrogen_bond_donors":
      return "# Hydrogen Bond Donors";
    case "hydrogen_bond_acceptors":
      return "# Hydrogen Bond Acceptors";
    case "sp3_carbons":
      return "# sp3 Carbons";
    case "il_molratio":
      return "Ionizable Lipid Ratio (%)";
    case "hl_molratio":
      return "Helper Lipid Ratio (%)";
    case "chl_molratio":
      return "Cholesterol Ratio (%)";
    case "peg_molratio":
      return "PEG Lipid Ratio (%)";
    case "il_to_nucleicacid_massratio":
      return "IL to nucleicacid Ratio (%)";
    default:
      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
};

const getDropdownPlaceholder = (key: string): string => {
  switch (key) {
    case "hl_name":
      return "Helper Lipid";
    case "chl_name":
      return "Cholesterol";
    case "peg_name":
      return "PEG Lipid";
    case "cargo_type":
      return "Cargo Type";
    case "cargo":
      return "Cargo ID";
    case "model_target":
      return "Delivery Target";
    case "experiment_method":
      return "Experiment Method";
    default:
      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
};

const CategorySection = memo(
  ({
    title,
    isOpen,
    toggleAccordion,
    icon,
    children,
    isDirty,
  }: {
    title: keyof AccordionState | "Record Status";
    isOpen: boolean;
    toggleAccordion: (key: keyof AccordionState | "Record Status") => void;
    icon: React.ReactNode;
    children: React.ReactNode;
    isDirty?: boolean;
  }) => (
    <li>
      <button
        type="button"
        className={`flex items-center w-full p-2 text-base transition duration-75 rounded-lg group hover:bg-gray-500 hover:text-white ${
          isDirty ? "bg-blue-100" : ""
        }`}
        onClick={() => toggleAccordion(title)}
      >
        {icon}
        <span className="flex-1 ml-3 text-sm text-left whitespace-nowrap">
          {title}
        </span>
        {isDirty && (
          <span className="flex-shrink-0 w-2 h-2 m-2 bg-blue-700 rounded-full"></span>
        )}
        <IconArrow isOpen={isOpen} />
      </button>
      <ul className={`${isOpen ? "block" : "hidden"}`}>{children}</ul>
      <hr className="m-2 border-gray-300" />
    </li>
  )
);

const DropdownItem = memo(
  ({
    id,
    options,
    value,
    onChange,
    placeholder,
    closeOtherDropdowns,
  }: {
    id: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (key: string, value: string) => void;
    placeholder: string;
    closeOtherDropdowns: (id: string) => void;
  }) => (
    <li key={id}>
      <Dropdown
        id={id}
        options={options}
        value={value}
        onChange={(value) => onChange(id, value)}
        placeholder={placeholder}
        closeOtherDropdowns={closeOtherDropdowns}
      />
    </li>
  )
);

const SliderItem = memo(
  ({
    id,
    value,
    onChange,
    label,
    sliderMin,
    sliderMax,
    step = 1,
    isDecimal = false,
  }: {
    id: string;
    value: RangeValue;
    onChange: (key: string) => (newRange: RangeValue) => void;
    label: string;
    sliderMin: number;
    sliderMax: number;
    step?: number;
    isDecimal?: boolean;
  }) => (
    <RangeSlider
      key={id}
      value={value}
      onChange={onChange(id)}
      label={label}
      sliderMin={sliderMin}
      sliderMax={sliderMax}
      step={step}
      isDecimal={isDecimal}
    />
  )
);

const TriStateToggleItem = memo(
  ({
    id,
    label,
    value,
    onChange,
  }: {
    id: string;
    label: string;
    value: boolean | undefined;
    onChange: (id: string, value: boolean | undefined) => void;
  }) => {
    const options = [
      {
        label: "Has",
        stateValue: true,
        activeClass: "bg-blue-600 text-white shadow",
      },
      {
        label: "Any",
        stateValue: undefined,
        activeClass: "bg-white text-gray-800 shadow-sm",
      },
      {
        label: "Lacks",
        stateValue: false,
        activeClass: "bg-red-600 text-white shadow",
      },
    ];

    const inactiveClass = "bg-transparent text-gray-500 hover:bg-gray-300/50";

    return (
      <li className="px-2 py-1 mb-1">
        <div className="flex items-center justify-between pl-4">
          <span className="text-sm text-gray-700">{label}</span>

          <div className="flex items-center space-x-1 rounded-full bg-gray-200 p-0.5">
            {options.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => onChange(id, option.stateValue)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-all duration-200 ease-in-out ${
                  value === option.stateValue
                    ? option.activeClass
                    : inactiveClass
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </li>
    );
  }
);

const biodegradableFilterKeys = [
  "has_ester",
  "has_carbonate",
  "has_disulfide",
] as const;
type BiodegradableFilterKey = (typeof biodegradableFilterKeys)[number];

const biodegradableFilterLabels: Record<BiodegradableFilterKey, string> = {
  has_ester: "Ester",
  has_carbonate: "Carbonate",
  has_disulfide: "Disulfide",
};

const SearchSideBar = () => {
  const { filterData, fetchFilterData, isLoading } = useFilterStore();
  const { setFilter, activeFilters, resetFilter } = useTableStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastFilterUpdate = useRef<number>(0);
  const isInitialURLParamsProcessed = useRef<boolean>(false);
  const isInitialFiltersFetched = useRef<boolean>(false);
  const skipNextURLUpdate = useRef<boolean>(false);

  const [isInitialized, setIsInitialized] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const [isOpen, setIsOpen] = useState<AccordionState & { "Record Status": boolean }>({
    "Library Code": true,
    Composition: false,
    Formulations: false,
    "Ionizable Lipid Properties": false,
    "Experimental Properties": false,
    Source: false,
    "Biodegradable substructures": false,
    "Record Status": false,
  });

  const [, setOpenDropdownId] = useState<string | null>(null);

  const categoryData = useMemo(() => {
    if (!filterData) {
      return {
        slidersByCategory: {
          ionizable_lipid_properties: [],
          formulations: [],
          experimental_properties: [],
        },
        dropdownsByCategory: {},
      };
    }

    const ionizableLipidOrder = [
      "nitrogen_count",
      "molecular_weight",
      "heavy_atoms",
      "rings",
      "aromatic_rings",
      "rotatable_bonds",
      "van_der_waals_molecular_volume",
      "topological_polar_surface_area",
      "hydrogen_bond_donors",
      "hydrogen_bond_acceptors",
      "logp",
      "molar_refractivity",
      "fraction_sp3_carbons",
      "sp3_carbons",
    ];

    const formulationsOrder = [
      "il_molratio",
      "hl_molratio",
      "chl_molratio",
      "peg_molratio",
      "il_to_nucleicacid_massratio",
    ];

    const slidersByCategory: Record<string, string[]> = {
      ionizable_lipid_properties: [],
      formulations: [],
      experimental_properties: [],
    };

    Object.entries(filterData.sliders || {}).forEach(([key, slider]) => {
      if (!slidersByCategory[slider.category]) {
        slidersByCategory[slider.category] = [];
      }
      slidersByCategory[slider.category].push(key);
    });

    slidersByCategory.ionizable_lipid_properties = ionizableLipidOrder.filter(
      (key) => slidersByCategory.ionizable_lipid_properties.includes(key)
    );

    slidersByCategory.formulations = formulationsOrder.filter((key) =>
      slidersByCategory.formulations.includes(key)
    );

    const dropdownsByCategory: Record<string, string[]> = {};
    Object.entries(filterData.dropdowns || {}).forEach(([key, dropdown]) => {
      if (!dropdownsByCategory[dropdown.category]) {
        dropdownsByCategory[dropdown.category] = [];
      }
      dropdownsByCategory[dropdown.category].push(key);
    });

    return { slidersByCategory, dropdownsByCategory };
  }, [filterData]);

  const updateURLWithFilters = useCallback(() => {
    if (skipNextURLUpdate.current) {
      skipNextURLUpdate.current = false;
      return;
    }

    const now = Date.now();
    if (now - lastFilterUpdate.current < 500) {
      return;
    }
    lastFilterUpdate.current = now;

    if (!isInitialFiltersFetched.current) {
      return;
    }

    const currentFilterParam = searchParams.get("filter");
    let currentFilters = {};

    if (currentFilterParam) {
      try {
        currentFilters = JSON.parse(decodeURIComponent(currentFilterParam));
      } catch (e) {}
    }

    const newFilters = Object.entries(activeFilters).reduce(
      (acc, [key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !(typeof value === "boolean" && value === false)
        ) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const isEqual = (obj1: any, obj2: any) => {
      if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

      return Object.keys(obj1).every((key) => {
        if (typeof obj1[key] === "object" && typeof obj2[key] === "object") {
          return JSON.stringify(obj1[key]) === JSON.stringify(obj2[key]);
        }
        return obj1[key] === obj2[key];
      });
    };

    if (!isEqual(currentFilters, newFilters)) {
      const newParams = new URLSearchParams(searchParams);

      if (Object.keys(newFilters).length > 0) {
        newParams.set("filter", JSON.stringify(newFilters));
      } else {
        newParams.delete("filter");
      }

      newParams.delete("experiment_id");
      newParams.delete("page");

      setSearchParams(newParams, { replace: true });
    }
  }, [activeFilters, searchParams, setSearchParams]);

  const handleResetFilter = useCallback(() => {
    resetFilter();
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("filter");
    newParams.delete("experiment_id");
    newParams.delete("page");
    newParams.delete("search");
    setSearchParams(newParams, { replace: true });

    if (filterData) {
      const initialValues: Record<string, any> = {};

      Object.keys(filterData.dropdowns || {}).forEach((key) => {
        initialValues[key] = "";
      });

      Object.keys(filterData.sliders || {}).forEach((key) => {
        const slider = filterData.sliders[key];
        initialValues[key] = {
          min: slider.range.min,
          max: slider.range.max,
          defaultMin: slider.range.min,
          defaultMax: slider.range.max,
        };
      });

      biodegradableFilterKeys.forEach((key) => {
        initialValues[key] = undefined;
      });

      setFilterValues(initialValues);
    } else {
      setFilterValues((prev) => {
        const resetValues: Record<string, any> = { ...prev };
        biodegradableFilterKeys.forEach((key) => {
          resetValues[key] = false;
        });
        return resetValues;
      });
    }
  }, [filterData, resetFilter, searchParams, setSearchParams]);

  const closeOtherDropdowns = useCallback((currentId: string) => {
    setOpenDropdownId(currentId);
  }, []);

  const toggleAccordion = useCallback((key: keyof AccordionState | "Record Status") => {
    setIsOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const handleDropdownChange = useCallback(
    (key: string, value: string) => {
      setFilterValues((prev) => ({
        ...prev,
        [key]: value,
      }));
      setFilter(key, value === "" ? null : value);
    },
    [setFilter]
  );

  const handleRangeChange = useCallback(
    (key: string) => (newRange: RangeValue) => {
      setFilterValues((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          min: newRange.min,
          max: newRange.max,
        },
      }));

      setFilter(key, {
        min: newRange.min,
        max: newRange.max,
      });
    },
    [setFilter]
  );

  const handleToggleChange = useCallback(
    (key: string, value: boolean | undefined) => {
      setFilterValues((prev) => ({ ...prev, [key]: value }));
      setFilter(key, value === undefined ? null : value);
    },
    [setFilter]
  );


  useEffect(() => {
    if (isInitialized && isInitialURLParamsProcessed.current) {
      updateURLWithFilters();
    }
  }, [activeFilters, isInitialized, updateURLWithFilters]);

  useEffect(() => {
    if (!filterData || isInitialURLParamsProcessed.current) return;

    const filterParam = searchParams.get("filter");
    let filtersFromURL: Record<string, any> = {};

    if (filterParam) {
      try {
        filtersFromURL = JSON.parse(decodeURIComponent(filterParam));
      } catch (error) {
        console.error("Failed to parse filter parameter:", error);
      }
    }

    const initialValuesFromURL: Record<string, any> = {};

    Object.keys(filterData.dropdowns || {}).forEach((key) => {
      const valueFromURL = filtersFromURL[key] ?? "";
      initialValuesFromURL[key] = valueFromURL;
      if (filtersFromURL.hasOwnProperty(key))
        setFilter(key, valueFromURL === "" ? null : valueFromURL);
    });

    Object.keys(filterData.sliders || {}).forEach((key) => {
      const sliderDefault = filterData.sliders[key].range;
      const valueFromURL = filtersFromURL[key];
      if (isRangeValue(valueFromURL)) {
        initialValuesFromURL[key] = {
          ...valueFromURL,
          defaultMin: sliderDefault.min,
          defaultMax: sliderDefault.max,
        };
        if (filtersFromURL.hasOwnProperty(key)) setFilter(key, valueFromURL);
      } else {
        initialValuesFromURL[key] = {
          min: sliderDefault.min,
          max: sliderDefault.max,
          defaultMin: sliderDefault.min,
          defaultMax: sliderDefault.max,
        };
      }
    });

    biodegradableFilterKeys.forEach((key) => {
      const valueFromURL = filtersFromURL[key];
      initialValuesFromURL[key] = valueFromURL;
      if (filtersFromURL.hasOwnProperty(key)) {
        setFilter(key, valueFromURL);
      }
    });

    setFilterValues(initialValuesFromURL);
    isInitialURLParamsProcessed.current = true;

    if (Object.keys(filtersFromURL).length > 0) {
      skipNextURLUpdate.current = true;
    }
  }, [searchParams, setFilter, filterData]);

  useEffect(() => {
    if (!isInitialized || !filterData) return;

    setFilterValues((prevFilterValues) => {
      const newFilterValues = { ...prevFilterValues };
      let changed = false;

      Object.keys(filterData.dropdowns || {}).forEach((key) => {
        const activeValue = activeFilters[key] ?? "";
        if (newFilterValues[key] !== activeValue) {
          newFilterValues[key] = activeValue;
          changed = true;
        }
      });

      Object.keys(filterData.sliders || {}).forEach((key) => {
        const sliderDefault = filterData.sliders[key].range;
        const activeValue = activeFilters[key];
        const currentLocalValue = prevFilterValues[key];

        let targetMin = sliderDefault.min;
        let targetMax = sliderDefault.max;

        if (isRangeValue(activeValue)) {
          targetMin = activeValue.min;
          targetMax = activeValue.max;
        }

        const newLocalSliderValue = {
          min: targetMin,
          max: targetMax,
          defaultMin: sliderDefault.min,
          defaultMax: sliderDefault.max,
        };

        if (
          JSON.stringify(currentLocalValue) !==
          JSON.stringify(newLocalSliderValue)
        ) {
          newFilterValues[key] = newLocalSliderValue;
          changed = true;
        }
      });

      biodegradableFilterKeys.forEach((key) => {
        const activeValue = activeFilters[key];
        if (newFilterValues[key] !== activeValue) {
          newFilterValues[key] = activeValue;
          changed = true;
        }
      });

      return changed ? newFilterValues : prevFilterValues;
    });
  }, [activeFilters, isInitialized, filterData]);

  useEffect(() => {
    if (!isInitialFiltersFetched.current) {
      fetchFilterData();
      isInitialFiltersFetched.current = true;
    }
  }, [fetchFilterData]);

  useEffect(() => {
    if (filterData && !isInitialized && isInitialURLParamsProcessed.current) {
      const initialValues: Record<string, any> = {};

      Object.keys(filterData.dropdowns || {}).forEach((key) => {
        initialValues[key] = activeFilters[key] ?? "";
      });

      Object.keys(filterData.sliders || {}).forEach((key) => {
        const slider = filterData.sliders[key];
        if (activeFilters[key] && isRangeValue(activeFilters[key])) {
          initialValues[key] = {
            ...activeFilters[key],
            defaultMin: slider.range.min,
            defaultMax: slider.range.max,
          };
        } else {
          initialValues[key] = {
            min: slider.range.min,
            max: slider.range.max,
            defaultMin: slider.range.min,
            defaultMax: slider.range.max,
          };
        }
      });

      biodegradableFilterKeys.forEach((key) => {
        initialValues[key] = activeFilters[key] ?? false;
      });

      setFilterValues(initialValues);
      setIsInitialized(true);
    }
  }, [filterData, isInitialized, activeFilters, isInitialURLParamsProcessed]);

  const isCategoryDirty = useCallback(
    (categoryKey: string, type: "sliders" | "dropdowns" | "combined") => {
      if (!filterData || !isInitialized) return false;

      if (categoryKey === "Ionizable Lipid Properties" && type === "combined") {
        const hasDirtySliders = categoryData.slidersByCategory?.[
          "ionizable_lipid_properties"
        ]?.some((key) => {
          const currentValue = filterValues[key];
          const defaultSliderValue = filterData.sliders?.[key]?.range;
          return (
            defaultSliderValue &&
            isRangeValue(currentValue) &&
            (currentValue.min !== defaultSliderValue.min ||
              currentValue.max !== defaultSliderValue.max)
          );
        });
        const hasDirtyToggles = biodegradableFilterKeys.some(
          (key) => filterValues[key] !== undefined
        );
        return !!hasDirtySliders || hasDirtyToggles;
      }

      const itemsInCategory =
        type === "sliders"
          ? categoryData.slidersByCategory[categoryKey]
          : categoryData.dropdownsByCategory[categoryKey];
      if (!itemsInCategory) return false;

      for (const key of itemsInCategory) {
        const currentValue = filterValues[key];
        if (
          type === "sliders" &&
          filterData.sliders?.[key] &&
          isRangeValue(currentValue)
        ) {
          const defaultSliderValue = filterData.sliders[key].range;
          if (
            currentValue.min !== defaultSliderValue.min ||
            currentValue.max !== defaultSliderValue.max
          ) {
            return true;
          }
        } else if (type === "dropdowns" && filterData.dropdowns?.[key]) {
          if (
            currentValue !== "" &&
            currentValue !== undefined &&
            currentValue !== null
          ) {
            return true;
          }
        }
      }
      return false;
    },
    [filterData, filterValues, categoryData, isInitialized]
  );

  if (isLoading || !filterData || !isInitialized) {
    return (
      <div className="antialiased bg-gray-50">
        <aside className="fixed top-0 left-0 z-40 h-screen transition-transform -translate-x-full bg-white border-r border-gray-200 w-72 pt-14 md:translate-x-0">
          <div className="h-full px-3 py-5 overflow-x-hidden overflow-y-scroll bg-gray-50"></div>
        </aside>
      </div>
    );
  }

  return (
    <div className="antialiased bg-gray-50">
      <aside
        className="fixed top-0 left-0 z-40 h-screen transition-transform -translate-x-full bg-white border-r border-gray-200 w-72 pt-14 md:translate-x-0"
        aria-label="Sidenav"
      >
        <div className="h-full px-3 py-5 overflow-x-hidden overflow-y-scroll bg-gray-50">
          <div className="flex items-center justify-between mb-4 space-x-2">
            <p className="ml-2 text-xl font-semibold text-gray-700">Filters</p>
            <button
              className="p-2 text-xs text-white bg-blue-800 rounded hover:bg-blue-900"
              onClick={handleResetFilter}
            >
              Reset
            </button>
          </div>
          <ul className="z-40 font-medium text-gray-600">
            <CategorySection
              title="Library Code"
              isOpen={isOpen["Library Code"]}
              toggleAccordion={toggleAccordion}
              icon={<LibraryIcon className="w-6 h-6 text-gray-600" />}
              isDirty={isCategoryDirty("library_code", "dropdowns")}
            >
              {categoryData.dropdownsByCategory["library_code"]?.map((key) => (
                <DropdownItem
                  key={key}
                  id={key}
                  options={[
                    { value: "", label: "ALL" },
                    ...filterData.dropdowns[key].choices,
                  ]}
                  value={filterValues[key] || ""}
                  onChange={handleDropdownChange}
                  placeholder="Library"
                  closeOtherDropdowns={closeOtherDropdowns}
                />
              ))}
            </CategorySection>

            <CategorySection
              title="Ionizable Lipid Properties"
              isOpen={isOpen["Ionizable Lipid Properties"]}
              toggleAccordion={toggleAccordion}
              icon={<LipidPropertiesIcon className="w-6 h-6 text-gray-600" />}
              isDirty={isCategoryDirty(
                "Ionizable Lipid Properties",
                "combined"
              )}
            >
              <div className="my-2 text-xs text-center text-gray-700">
                Biodegradable Substructure
              </div>
              {biodegradableFilterKeys.map((key) => (
                <TriStateToggleItem
                  key={key}
                  id={key}
                  label={biodegradableFilterLabels[key]}
                  value={filterValues[key] as boolean | undefined}
                  onChange={handleToggleChange}
                />
              ))}
              <div className="my-2 border-b-2 border-gray-200"></div>
              {categoryData.slidersByCategory[
                "ionizable_lipid_properties"
              ]?.map((key) => (
                <SliderItem
                  key={key}
                  id={key}
                  value={filterValues[key]}
                  onChange={handleRangeChange}
                  label={getSliderLabel(key)}
                  sliderMin={filterData.sliders[key].range.min}
                  sliderMax={filterData.sliders[key].range.max}
                  step={
                    key === "fraction_sp3_carbons"
                      ? 0.01
                      : key === "logp" || key === "molar_refractivity"
                      ? 0.01
                      : 1
                  }
                  isDecimal={
                    key === "fraction_sp3_carbons" ||
                    key === "logp" ||
                    key === "molar_refractivity"
                  }
                />
              ))}
            </CategorySection>

            <CategorySection
              title="Composition"
              isOpen={isOpen["Composition"]}
              toggleAccordion={toggleAccordion}
              icon={<CompositionIcon className="w-6 h-6 text-gray-600" />}
              isDirty={isCategoryDirty("composition", "dropdowns")}
            >
              {["hl_name", "chl_name", "peg_name"]
                .filter((key) =>
                  categoryData.dropdownsByCategory["composition"]?.includes(key)
                )
                .map((key) => (
                  <DropdownItem
                    key={key}
                    id={key}
                    options={[
                      { value: "", label: "ALL" },
                      ...(filterData.dropdowns[key]?.choices || []),
                    ]}
                    value={filterValues[key] || ""}
                    onChange={handleDropdownChange}
                    placeholder={getDropdownPlaceholder(key)}
                    closeOtherDropdowns={closeOtherDropdowns}
                  />
                ))}
            </CategorySection>

            <CategorySection
              title="Formulations"
              isOpen={isOpen["Formulations"]}
              toggleAccordion={toggleAccordion}
              icon={<FormulationsIcon className="w-6 h-6 text-gray-600" />}
              isDirty={isCategoryDirty("formulations", "sliders")}
            >
              {categoryData.slidersByCategory["formulations"]?.map((key) => (
                <SliderItem
                  key={key}
                  id={key}
                  value={filterValues[key]}
                  onChange={handleRangeChange}
                  label={getSliderLabel(key)}
                  sliderMin={filterData.sliders[key].range.min}
                  sliderMax={filterData.sliders[key].range.max}
                  step={0.01}
                  isDecimal={true}
                />
              ))}
            </CategorySection>

            <CategorySection
              title="Experimental Properties"
              isOpen={isOpen["Experimental Properties"]}
              toggleAccordion={toggleAccordion}
              icon={
                <ExperimentalPropertiesIcon className="w-6 h-6 text-gray-600" />
              }
              isDirty={
                isCategoryDirty("experimental_properties", "dropdowns") ||
                isCategoryDirty("experimental_properties", "sliders")
              }
            >
              {[
                "cargo_type",
                "cargo",
                "model",
                "model_target",
                "route_of_administration",
                "experiment_method",
                "experiment_batching",
              ]
                .filter((key) =>
                  categoryData.dropdownsByCategory[
                    "experimental_properties"
                  ]?.includes(key)
                )
                .map((key) => (
                  <DropdownItem
                    key={key}
                    id={key}
                    options={[
                      { value: "", label: "ALL" },
                      ...(filterData.dropdowns[key]?.choices || []),
                    ]}
                    value={filterValues[key] || ""}
                    onChange={handleDropdownChange}
                    placeholder={getDropdownPlaceholder(key)}
                    closeOtherDropdowns={closeOtherDropdowns}
                  />
                ))}
              {categoryData.slidersByCategory["experimental_properties"]?.map(
                (key) => (
                  <SliderItem
                    key={key}
                    id={key}
                    value={filterValues[key]}
                    onChange={handleRangeChange}
                    label={getSliderLabel(key)}
                    sliderMin={filterData.sliders[key].range.min}
                    sliderMax={filterData.sliders[key].range.max}
                  />
                )
              )}
            </CategorySection>
            
            {process.env.REACT_APP_ENABLE_ADMIN === "True" && (
              <CategorySection
                title="Record Status"
                isOpen={isOpen["Record Status"]}
                toggleAccordion={toggleAccordion}
                icon={
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                isDirty={filterValues["status"] !== undefined && filterValues["status"] !== ""}
              >
                <li className="px-2 py-2 mb-1">
                  <div className="flex items-center justify-between pl-4">
                    <span className="text-sm text-gray-700">Status</span>
                    <div className="flex items-center space-x-1 rounded-full bg-gray-200 p-0.5">
                      {[
                        { label: "All", value: "" },
                        { label: "Draft", value: "draft" },
                        { label: "Published", value: "published" },
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => handleDropdownChange("status", opt.value)}
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-all duration-200 ease-in-out ${
                            (filterValues["status"] || "") === opt.value
                              ? "bg-blue-600 text-white shadow"
                              : "bg-transparent text-gray-500 hover:bg-gray-300/50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </li>
              </CategorySection>
            )}

          </ul>
        </div>
      </aside>
    </div>
  );
};

export default SearchSideBar;
